import { mediaUrl } from '../../lib/media-url.js';
import { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2, ArrowRight, Package } from 'lucide-react';
import { attachEscapeDismiss } from '../../lib/escape-dismiss.js';
import { formatCurrency } from '../../lib/utils.js';
import { isPlainLeftClick } from '../../lib/click.js';

function getProductImage(prod) {
  if (prod?.primaryImageUrl) return prod.primaryImageUrl;
  if (prod?.image_url) return prod.image_url;
  if (Array.isArray(prod?.images) && prod.images.length > 0) {
    const first = prod.images[0];
    return typeof first === 'string' ? first : first?.url || '';
  }
  if (prod?.imagesJson) {
    try {
      const parsed = JSON.parse(prod.imagesJson);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const first = parsed[0];
        return typeof first === 'string' ? first : first?.url || '';
      }
    } catch {}
  }
  return '';
}

function getProductPrice(prod) {
  const cents = prod?.price?.cents !== undefined
    ? Number(prod.price.cents)
    : (prod?.base_price_cents !== undefined
      ? Number(prod.base_price_cents)
      : (prod?.effective_price_cents !== undefined ? Number(prod.effective_price_cents) : 0));
  if (prod?.price?.formatted) return prod.price.formatted;
  return cents > 0 ? formatCurrency(cents) : 'Auf Anfrage';
}

export function SearchOverlay({
  isOpen,
  onClose,
  tenant,
  onNavigate,
  onSearch,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    return attachEscapeDismiss(document, () => {
      if (onClose) onClose();
    });
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setQuery('');
      setResults([]);
      setLoading(false);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults([]);
      setLoading(false);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      return;
    }

    setLoading(true);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const tenantId = tenant?.id || '';
        const params = new URLSearchParams({
          search: term,
          limit: '8',
        });
        if (tenantId) {
          params.set('tenant_id', tenantId);
        }

        const res = await fetch(`/api/products?${params.toString()}`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) {
          throw new Error(`Suchanfrage fehlgeschlagen (${res.status})`);
        }

        const data = await res.json();
        setResults(Array.isArray(data?.products) ? data.products : []);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setResults([]);
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [query, tenant?.id]);

  if (!isOpen) return null;

  const handleProductClick = (e, prod) => {
    if (!isPlainLeftClick(e)) return;
    e.preventDefault();
    const slugOrSku = prod.slug || prod.sku || prod.id;
    if (onNavigate) {
      onNavigate(`/products/${slugOrSku}`);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleCatalogSubmit = (e) => {
    if (e) e.preventDefault();
    const term = query.trim();
    if (onSearch) onSearch(term);
    if (onNavigate) {
      onNavigate(term ? `/catalog?search=${encodeURIComponent(term)}` : '/catalog');
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      data-testid="search-overlay"
      className="fixed inset-0 z-50 flex flex-col items-center justify-start pt-20 sm:pt-24 px-4"
    >
      {/* Backdrop */}
      <div
        data-testid="search-overlay-backdrop"
        className="fixed inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Produktsuche"
        className="relative z-10 w-full max-w-[720px] rounded-2xl border border-[var(--border)] bg-white shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-150"
      >
        {/* Search Input Bar */}
        <form onSubmit={handleCatalogSubmit} className="relative flex items-center border-b border-[var(--border)] px-4 py-3">
          <Search className="h-5 w-5 text-muted-foreground mr-3 shrink-0" />
          <input
            ref={inputRef}
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Katalog nach Artikelnummer, Hersteller oder Begriff durchsuchen..."
            className="flex-1 bg-transparent text-sm text-[var(--foreground)] placeholder:text-muted-foreground focus:outline-none"
            aria-label="Suchbegriff eingeben"
          />
          {loading && (
            <Loader2 className="h-4 w-4 animate-spin text-[var(--primary)] mr-2 shrink-0" />
          )}
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-1 text-muted-foreground hover:text-[var(--foreground)] rounded-full hover:bg-[var(--surface-container-low)] mr-1 cursor-pointer"
              aria-label="Eingabe löschen"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-muted-foreground hover:text-[var(--foreground)] hover:bg-[var(--surface-container-low)] cursor-pointer"
            aria-label="Suche schließen"
          >
            ESC
          </button>
        </form>

        {/* Live Search Results */}
        <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
          {query.trim() === '' ? (
            <div className="p-8 text-center text-xs text-muted-foreground">
              Geben Sie eine Artikelnummer, einen Herstellernamen oder ein Stichwort ein, um unser Sortiment live zu durchsuchen.
            </div>
          ) : results.length > 0 ? (
            <div className="divide-y divide-[var(--border)]">
              {results.map((prod) => {
                const imgUrl = getProductImage(prod);
                const priceStr = getProductPrice(prod);
                const prodUrl = `/products/${prod.slug || prod.sku || prod.id}`;
                return (
                  <a
                    key={prod.id || prod.sku}
                    href={prodUrl}
                    onClick={(e) => handleProductClick(e, prod)}
                    className="flex items-center gap-4 p-3.5 hover:bg-[var(--surface-container-low,#f8fafc)] transition-colors group cursor-pointer"
                  >
                    <div className="h-12 w-12 rounded-lg bg-[var(--surface-container-high,#f1f5f9)] p-1 flex items-center justify-center shrink-0 overflow-hidden border border-[var(--border)]">
                      {imgUrl ? (
                        <img
                          src={mediaUrl(imgUrl)}
                          alt={prod.name || 'Produkt'}
                          className="h-full w-full object-contain"
                          loading="lazy"
                        />
                      ) : (
                        <Package className="h-6 w-6 text-muted-foreground/60" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <h4 className="text-xs font-bold text-[var(--foreground)] group-hover:text-[var(--primary)] truncate transition-colors">
                          {prod.name}
                        </h4>
                        <span className="text-xs font-bold text-[var(--foreground)] shrink-0">
                          {priceStr}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-[var(--secondary)]">
                        {prod.sku && <span className="font-mono">Art.-Nr. {prod.sku}</span>}
                        {prod.manufacturer && (
                          <>
                            <span>&bull;</span>
                            <span className="truncate">{prod.manufacturer}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          ) : !loading ? (
            <div className="p-8 text-center text-xs text-muted-foreground">
              Keine Produkte für „<span className="font-semibold text-[var(--foreground)]">{query}</span>“ gefunden.
            </div>
          ) : null}
        </div>

        {/* Catalog Link Footer */}
        {query.trim() !== '' && (
          <div className="border-t border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] p-3">
            <a
              href={`/catalog?search=${encodeURIComponent(query.trim())}`}
              onClick={handleCatalogSubmit}
              className="flex items-center justify-between text-xs font-bold text-[var(--primary)] hover:underline cursor-pointer"
            >
              <span>Alle Ergebnisse im Katalog ansehen</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
